import { createClient } from '@supabase/supabase-js'

// Project credentials — read from environment variables only.
// NEVER hardcode Supabase URL/keys or webhook URLs in source.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

// No fallbacks: if the environment isn't configured, the app should fail
// loudly and immediately rather than silently connecting to a fake/dead
// placeholder host (which would just hang and look like a stuck loading
// spinner instead of a clear error).
export const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY)

if (!isSupabaseConfigured) {
  console.error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. ' +
    'Set both in your environment — see .env.example. The app cannot load or ' +
    'save any data until this is configured; there is no fallback data source.'
  )
}

// Only construct a real client when actually configured. If not configured,
// `supabase` is null and every hook checks `isSupabaseConfigured` before
// using it, so callers see an explicit configuration error instead of a
// request hanging against a placeholder URL that was never real.
export const supabase = isSupabaseConfigured
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true
      }
    })
  : null

// Admin authorization is enforced entirely server-side via Supabase Auth +
// Row Level Security (see supabase/migrations/002_admin_auth.sql). There is
// no client-side admin password: this helper simply checks — via a real,
// authenticated request — whether the current logged-in user is listed in
// the `admin_users` table. Even if this check were bypassed or spoofed in
// the browser, RLS policies in Postgres independently refuse any write from
// a non-admin, so this is a UI convenience, not the security boundary.
export const isCurrentUserAdmin = async () => {
  if (!isSupabaseConfigured) return false

  // No getSession() call here anymore. Every caller of this function
  // already has a confirmed session before calling it (useAdminAuth only
  // ever calls this once supabase.auth.getSession() or
  // signInWithPassword() has already resolved), so re-checking it here
  // was a redundant network round trip that serialized in front of the
  // is_admin() RPC below, roughly doubling the time-to-render on both
  // sign-in and refresh. supabase-js attaches whatever access token it
  // currently holds to the RPC call automatically — if there truly is no
  // session, the RPC simply runs unauthenticated and is_admin() correctly
  // evaluates auth.uid() as null (i.e. not admin), so this is not a
  // security gap.

  // Use the is_admin() RPC (SECURITY DEFINER, defined in the staging SQL)
  // rather than querying admin_users directly. The "Admins can view admin
  // list" RLS policy on admin_users only allows a SELECT if the requesting
  // user is *already* found in admin_users — which makes a direct client
  // query circular and always return zero rows for a real admin checking
  // their own status. is_admin() runs with definer privileges and sidesteps
  // that policy entirely, so it correctly reports true/false regardless.
  const { data, error } = await supabase.rpc('is_admin')

  if (error) {
    console.error('Failed to check admin status:', error.message)
    return false
  }
  return !!data
}
